/**
 * Utility functions for date and time operations
 */

/**
 * Converts a date to a human-readable time ago string
 * @param date - The date to convert
 * @returns A string representing how long ago the date was
 */
export function getTimeAgo(date: Date): string {
  const now = new Date();
  const diffInDays = Math.floor(
    (now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24),
  );

  if (diffInDays === 0) return 'Today';
  if (diffInDays === 1) return '1 day ago';
  if (diffInDays < 7) return `${diffInDays} days ago`;
  if (diffInDays < 30) {
    const weeks = Math.floor(diffInDays / 7);
    if (weeks === 1) return '1 week ago';
    return `${weeks} weeks ago`;
  }
  if (diffInDays < 365) {
    const months = Math.floor(diffInDays / 30);
    if (months === 1) return '1 month ago';
    return `${months} months ago`;
  }
  const years = Math.floor(diffInDays / 365);
  if (years === 1) return '1 year ago';
  return `${years} years ago`;
}

/**
 * Converts a date string to a human-readable time ago string
 * @param dateString - The date string to convert
 * @returns A string representing how long ago the date was
 */
export function getTimeAgoFromString(dateString: string): string {
  return getTimeAgo(new Date(dateString));
}

/**
 * Converts a date string to a human-readable time ago string or exact date
 * Shows exact date for articles older than 1 month
 * @param dateString - The date string to convert
 * @returns A string representing how long ago the date was or the exact date
 */
export function getTimeAgoOrExactDate(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffInDays = Math.floor(
    (now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24),
  );

  // If article is older than 1 month (approximately 30 days), show exact date
  if (diffInDays > 30) {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }

  // Otherwise, show time ago format
  return getTimeAgo(date);
}
